import { Component, inject, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatDividerModule } from '@angular/material/divider';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import {
    FormBuilder,
    FormGroup,
    FormsModule,
    ReactiveFormsModule,
} from '@angular/forms';
import {
    IFloatingFilterParams,
    ISimpleFilter,
    TextFilterModel,
} from 'ag-grid-community';
import { IFloatingFilterAngularComp } from 'ag-grid-angular';

import { InputComponent } from '@shared/input/input.component';
import { SliderComponent } from '@shared/range-slider/range-slider.component';
import { TextButtonComponent } from '@shared/button/text-button/text-button.component';
import { FlatButtonComponent } from '@shared/button/flat-button/flat-button.component';
import { StopClickDirective } from '@directives/stop-click.directive';
import { Range } from '@models/shared/range';

export interface CustomParams {
    availableRange: Range<number>;
    title?: string;
    inputLabel?: string;
}

@Component({
    templateUrl: './range-filter.component.html',
    styleUrl: 'range-filter.component.scss',
    imports: [
        CommonModule,
        MatMenuModule,
        MatIconModule,
        MatDividerModule,
        InputComponent,
        TextButtonComponent,
        FlatButtonComponent,
        SliderComponent,
        ReactiveFormsModule,
        FormsModule,
        StopClickDirective,
    ],
})
export class RangeFilterComponent implements IFloatingFilterAngularComp {
    @ViewChild(MatMenuTrigger) trigger: MatMenuTrigger | undefined;
    availableRange: Range<number> = new Range(0, 180);
    currentValue: Range<number> | undefined;
    form!: FormGroup;
    params!: IFloatingFilterParams<ISimpleFilter> & CustomParams;
    private formChanged: boolean = false;

    private readonly formBuilder = inject(FormBuilder);

    get minPlaceholder() {
        return this.availableRange.min !== null
            ? this.availableRange.min.toString()
            : '';
    }

    get maxPlaceholder() {
        return this.availableRange.max !== null
            ? this.availableRange.max.toString()
            : '';
    }

    get filterText() {
        if (this.formChanged && this.currentValue) {
            const { min, max } = this.currentValue;
            return min === max ? min : min + '-' + max;
        } else {
            return 'Filter';
        }
    }

    agInit(params: IFloatingFilterParams<ISimpleFilter> & CustomParams): void {
        this.params = params;
        this.setAvailableRange();
        this.initFormSafely();
    }

    onParentModelChanged(_parentModel: TextFilterModel): void {
        if (_parentModel?.filter) {
            const filter = JSON.parse(_parentModel.filter);
            this.ensureForm();
            this.form.patchValue({ min: filter.min, max: filter.max });
            this.currentValue = new Range(filter.min, filter.max);
            this.formChanged = true;
        } else {
            this.formChanged = false;
            this.currentValue = undefined;
            this.initFormSafely();
        }
    }

    refresh(params: IFloatingFilterParams<ISimpleFilter> & CustomParams) {
        this.params = params;
        this.setAvailableRange();

        if (this.currentValue) {
            this.ensureForm();
            this.form = this.formBuilder.group({
                min: [this.form.value.min],
                max: [this.form.value.max],
            });
        } else this.initFormSafely();
    }

    reset() {
        this.params.api?.refreshHeader();
        const coldId = this.params.column.getColId();
        const newFilterModel = this.params.api.getFilterModel();
        newFilterModel[coldId] = null;
        this.params.api.setFilterModel(newFilterModel);
        this.params.api?.onFilterChanged();
    }

    close() {
        if (this.trigger) this.trigger.closeMenu();
    }

    apply() {
        this.formChanged = true;
        this.onFilterChanged();
        if (this.trigger) this.trigger.closeMenu();
    }

    private onFilterChanged() {
        this.params.parentFilterInstance((instance: ISimpleFilter) => {
            const minAndMax = this.formChanged
                ? this.getRangeValues()
                : undefined;

            instance.onFloatingFilterChanged('contains', minAndMax);
        });
    }

    private initFormSafely() {
        this.form = this.formBuilder.group({
            min: [this.availableRange.min],
            max: [this.availableRange.max],
        });
    }

    private ensureForm() {
        if (!this.form) {
            this.initFormSafely();
        }
    }

    private getRangeValues() {
        const { min, max } = this.form.value;
        let _min = parseFloat(min);
        let _max = parseFloat(max);

        if (_min > _max) _min = _max;
        if (_max <= _min) _max = _min;
        if (_min < this.availableRange.min) _min = this.availableRange.min;
        if (_max > this.availableRange.max) _max = this.availableRange.max;

        this.form.patchValue({ min: _min, max: _max });
        this.currentValue = new Range(_min, _max);
        const minAndMax = JSON.stringify(this.currentValue);

        return minAndMax;
    }

    private setAvailableRange() {
        this.availableRange = this.params.availableRange.max
            ? this.params.availableRange
            : this.availableRange;
    }
}
